(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const emit = (root, action, details = {}) => {
    const payload = {
      event: `pdp2_${action}`,
      pdp_system: 'zentein_pdp_2',
      product_id: root.dataset.productId,
      product_handle: root.dataset.productHandle,
      variant_id: root.dataset.variantId,
      currency: root.dataset.currency,
      ...details,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent('zentein:pdp2', { detail: payload }));
  };

  const initGallery = (root) => {
    const rail = root.querySelector('[data-pdp2-gallery]');
    const slides = [...root.querySelectorAll('[data-pdp2-slide]')];
    const buttons = [...root.querySelectorAll('[data-pdp2-gallery-target]')];
    if (!rail || slides.length === 0 || buttons.length === 0) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const select = (index, track = true) => {
      const slide = slides[index];
      if (!slide) return;
      rail.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
      });
      if (track) emit(root, 'media_interaction', { media_index: index + 1 });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => select(Number(button.dataset.pdp2GalleryTarget)));
    });

    let frame = 0;
    rail.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / rail.clientWidth)));
        buttons.forEach((button, buttonIndex) => {
          button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
        });
      });
    }, { passive: true });
  };

  const initPurchase = (root) => {
    const tierInputs = [...root.querySelectorAll('[data-pdp2-tier]')];
    const quantityInput = root.querySelector('[data-pdp2-quantity]');
    const primaryButtonLabel = root.querySelector('[data-pdp2-button-label]');
    const selection = root.querySelector('[data-pdp2-selection]');
    const primaryForm = root.querySelector('[data-pdp2-primary-form] form');
    const purchasePanel = root.querySelector('[data-pdp2-purchase-panel]');
    const sticky = root.querySelector('[data-pdp2-sticky]');
    const stickyLabel = root.querySelector('[data-pdp2-sticky-label]');
    const stickyButton = root.querySelector('[data-pdp2-sticky-submit]');

    const syncTier = (input, shouldTrack = true) => {
      if (!input || !quantityInput) return;
      const quantity = Number(input.value);
      const noun = quantity === 1 ? 'BOX' : 'BOXES';
      quantityInput.value = String(quantity);
      if (primaryButtonLabel) primaryButtonLabel.textContent = `ADD ${quantity} ${noun} TO CART — ${input.dataset.total}`;
      if (selection) selection.textContent = `${quantity} ${noun.toLowerCase()} · ${input.dataset.bars} bars · ${input.dataset.perBar}/bar`;
      if (stickyLabel) stickyLabel.textContent = `${quantity} ${noun.toLowerCase()} · ${input.dataset.total}`;
      if (stickyButton) stickyButton.textContent = `ADD ${quantity}`;
      if (shouldTrack) {
        emit(root, 'quantity_selected', {
          quantity,
          discount_percent: Number(input.dataset.discount),
          displayed_total: input.dataset.total,
        });
      }
    };

    tierInputs.forEach((input) => input.addEventListener('change', () => syncTier(input)));
    syncTier(tierInputs.find((input) => input.checked) || tierInputs[0], false);

    primaryForm?.addEventListener('submit', () => {
      const selected = tierInputs.find((input) => input.checked) || tierInputs[0];
      emit(root, 'add_to_cart', {
        source: 'purchase_hero',
        quantity: Number(quantityInput?.value || 1),
        discount_percent: Number(selected?.dataset.discount || 0),
      });
    });

    stickyButton?.addEventListener('click', () => {
      emit(root, 'sticky_add_to_cart', { quantity: Number(quantityInput?.value || 1) });
      primaryForm?.requestSubmit();
    });

    if (purchasePanel && sticky) {
      let stickyFrame = 0;
      const updateSticky = () => {
        cancelAnimationFrame(stickyFrame);
        stickyFrame = requestAnimationFrame(() => {
          const panel = purchasePanel.getBoundingClientRect();
          const page = root.getBoundingClientRect();
          const show = window.innerWidth < 750 && panel.bottom < 0 && page.bottom > 120;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      };
      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
      updateSticky();
    }
  };

  const initInteractions = (root) => {
    root.querySelectorAll('[data-pdp2-detail]').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        emit(root, detail.dataset.pdp2Detail, {
          label: detail.dataset.pdp2Label || detail.querySelector('summary')?.innerText.trim() || '',
        });
      });
    });

    root.querySelectorAll('[data-pdp2-track]').forEach((element) => {
      element.addEventListener('click', () => {
        emit(root, element.dataset.pdp2Track, {
          label: element.dataset.pdp2Label || element.innerText.trim().slice(0, 120),
        });
      });
    });

    root.querySelectorAll('[data-pdp2-cross-sell-form] form').forEach((form) => {
      form.addEventListener('submit', () => {
        const wrapper = form.closest('[data-pdp2-cross-sell-form]');
        emit(root, wrapper?.dataset.pdp2Role === 'sampler' ? 'sampler_add' : 'cross_sell_add', {
          recommended_product_id: wrapper?.dataset.productId,
          recommended_product_handle: wrapper?.dataset.productHandle,
        });
      });
    });
  };

  ready(() => {
    document.querySelectorAll('[data-pdp2-root]').forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      document.body.classList.add('pdp2-active');
      initGallery(root);
      initPurchase(root);
      initInteractions(root);
      emit(root, 'view', { template: root.dataset.productHandle || 'pdp-2' });
    });
  });
})();

/* PDP 2.1 conversion, pacing and interaction pass — staging theme only */
(() => {
  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const initPdpEnhancements = (root) => {
    if (!root || root.dataset.pdp2ConversionEnhanced === 'true') return;
    root.dataset.pdp2ConversionEnhanced = 'true';
    document.body.classList.add('pdp2-page');

    const allSections = () => Array.from(root.querySelectorAll('section'));
    const sectionWith = (selector) => allSections().find((section) => section.querySelector(selector));
    const hero = root.querySelector('.pdp2__hero');
    const experienceSection = sectionWith('.pdp2-experience');
    const freshnessSection = sectionWith('.pdp2-freshness');
    const historySection = sectionWith('.pdp2-history');
    const waysSection = sectionWith('.pdp2-ways');
    const ingredientsSection = sectionWith('.pdp2-ingredients');
    const nutritionSection = sectionWith('.pdp2-nutrition');
    const sourceProofSection = sectionWith('.pdp2-reviews');
    const comparisonSection = sectionWith('.pdp2-compare-wrap');
    const crossSellSection = sectionWith('.pdp2-cross-sells');
    const faqSection = sectionWith('.pdp2-faq');
    const finalSection = sectionWith('.pdp2-final');

    if (experienceSection) experienceSection.id = 'pdp2-experience';
    if (historySection) historySection.id = 'pdp2-story';
    if (waysSection) waysSection.id = 'pdp2-ways';
    if (ingredientsSection) ingredientsSection.id = 'pdp2-ingredients';
    if (nutritionSection) nutritionSection.id = 'pdp2-nutrition';
    if (comparisonSection) comparisonSection.id = 'pdp2-compare';
    if (crossSellSection) crossSellSection.id = 'pdp2-cross-sells';
    if (faqSection) faqSection.id = 'pdp2-faq';

    const waysEyebrow = waysSection && waysSection.querySelector('.pdp2__eyebrow');
    if (waysEyebrow) waysEyebrow.textContent = 'More ways to enjoy';

    const firstIngredientTitle = ingredientsSection && ingredientsSection.querySelector('.pdp2-ingredient h3');
    if (firstIngredientTitle && /bovine gelatin/i.test(firstIngredientTitle.textContent)) {
      firstIngredientTitle.textContent = 'Bovine collagen (gelatin)';
    }

    const sourceReviews = sourceProofSection
      ? Array.from(sourceProofSection.querySelectorAll('.pdp2-reviews > article'))
      : [];
    const reviewCount = Number.parseInt(root.dataset.ratingCount || '0', 10);
    const allReviewsLabel = reviewCount > 0
      ? 'Read all ' + reviewCount + ' reviews →'
      : 'Read customer reviews →';

    const createProofBlock = (review, options) => {
      if (!review) return null;
      const section = document.createElement('section');
      section.className = 'pdp2-proof-block' + (options.later ? ' pdp2-proof-block--later' : '');
      section.setAttribute('aria-label', options.ariaLabel);

      const shell = document.createElement('div');
      shell.className = 'pdp2__shell pdp2-proof-block__shell';

      const intro = document.createElement('p');
      intro.className = 'pdp2-proof-block__intro';
      intro.innerHTML =
        '<span class="pdp2__eyebrow">' + options.eyebrow + '</span>' +
        '<strong>' + options.title + '</strong>';

      const clonedReview = review.cloneNode(true);
      const link = document.createElement('a');
      link.className = 'pdp2-proof-block__link';
      link.href = '#pdp2-reviews';
      link.textContent = options.linkText;
      link.dataset.pdp2Track = 'review_interaction';
      link.dataset.pdp2Label = options.ariaLabel;

      shell.append(intro, clonedReview, link);
      section.append(shell);
      return section;
    };

    const earlyProof = createProofBlock(sourceReviews[0], {
      eyebrow: 'Verified customer proof',
      title: 'Loved for taste, texture and convenience.',
      linkText: allReviewsLabel,
      ariaLabel: 'Early verified customer review',
      later: false
    });
    if (hero && earlyProof) hero.after(earlyProof);

    const pageNav = document.createElement('nav');
    pageNav.className = 'pdp2-page-nav';
    pageNav.setAttribute('aria-label', 'On this product page');
    const storyNavLabel = root.classList.contains('pdp2--strawberry')
      ? 'Why Strawberry Blondie'
      : 'Our story';
    pageNav.innerHTML =
      '<div class="pdp2__shell pdp2-page-nav__inner">' +
      '<a href="#pdp2-experience">Taste + texture</a>' +
      '<a href="#pdp2-story">' + storyNavLabel + '</a>' +
      '<a href="#pdp2-ways">Ways to enjoy</a>' +
      '<a href="#pdp2-ingredients">Ingredients</a>' +
      '<a href="#pdp2-nutrition">Nutrition</a>' +
      '<a href="#pdp2-reviews">Reviews</a>' +
      '</div>';
    if (earlyProof) earlyProof.after(pageNav);
    else if (hero) hero.after(pageNav);

    if (experienceSection && freshnessSection) {
      const experienceStack = document.createElement('div');
      experienceStack.className = 'pdp2-product-experience';
      experienceSection.before(experienceStack);
      experienceStack.append(experienceSection, freshnessSection);
    }

    const laterProof = createProofBlock(sourceReviews[2] || sourceReviews[1], {
      eyebrow: 'Made for real days',
      title: 'Easy to take with you.',
      linkText: 'More customer stories →',
      ariaLabel: 'Later verified customer review',
      later: true
    });
    if (waysSection && laterProof) waysSection.after(laterProof);
    if (sourceProofSection) sourceProofSection.classList.add('pdp2-source-proof');

    const gallery = root.querySelector('.pdp2-gallery');
    const galleryRail = gallery && gallery.querySelector('.pdp2-gallery__rail');
    const gallerySlides = gallery ? Array.from(gallery.querySelectorAll('.pdp2-gallery__slide')) : [];
    const galleryThumbs = gallery ? Array.from(gallery.querySelectorAll('.pdp2-gallery__thumb')) : [];
    if (gallery && galleryRail && gallerySlides.length) {
      const progress = document.createElement('div');
      progress.className = 'pdp2-gallery__progress';
      progress.setAttribute('role', 'status');
      progress.setAttribute('aria-live', 'polite');
      progress.innerHTML =
        '<span class="pdp2-gallery__progress-count">1 / ' + gallerySlides.length + '</span>' +
        '<span class="pdp2-gallery__progress-track" aria-hidden="true">' +
        '<span class="pdp2-gallery__progress-fill"></span></span>';
      const hint = gallery.querySelector('.pdp2-gallery__hint');
      (hint || galleryRail).after(progress);

      const count = progress.querySelector('.pdp2-gallery__progress-count');
      const fill = progress.querySelector('.pdp2-gallery__progress-fill');
      const setGalleryIndex = (index) => {
        const safeIndex = Math.max(0, Math.min(gallerySlides.length - 1, index));
        count.textContent = (safeIndex + 1) + ' / ' + gallerySlides.length;
        fill.style.width = (((safeIndex + 1) / gallerySlides.length) * 100) + '%';
        galleryThumbs.forEach((thumb, thumbIndex) => {
          const current = thumbIndex === safeIndex;
          thumb.classList.toggle('is-current', current);
          thumb.setAttribute('aria-pressed', String(current));
        });
      };
      galleryThumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => setGalleryIndex(index));
      });
      let galleryFrame = 0;
      galleryRail.addEventListener('scroll', () => {
        if (galleryFrame) cancelAnimationFrame(galleryFrame);
        galleryFrame = requestAnimationFrame(() => {
          const width = galleryRail.clientWidth || 1;
          setGalleryIndex(Math.round(galleryRail.scrollLeft / width));
        });
      }, { passive: true });
      setGalleryIndex(0);
    }

    if (waysSection) {
      const ways = waysSection.querySelector('.pdp2-ways');
      const panels = ways ? Array.from(ways.querySelectorAll('.pdp2-way')) : [];
      if (ways && panels.length) {
        const tabs = document.createElement('div');
        tabs.className = 'pdp2-ways-nav';
        tabs.setAttribute('role', 'tablist');
        tabs.setAttribute('aria-label', 'Choose a way to enjoy your bar');

        const stage = document.createElement('div');
        stage.className = 'pdp2-ways-stage';
        const buttons = [];

        const activate = (index, moveFocus) => {
          panels.forEach((panel, panelIndex) => {
            const active = panelIndex === index;
            panel.hidden = !active;
            panel.classList.toggle('is-active', active);
            buttons[panelIndex].setAttribute('aria-selected', String(active));
            buttons[panelIndex].tabIndex = active ? 0 : -1;
          });
          if (moveFocus) buttons[index].focus();
        };

        panels.forEach((panel, index) => {
          const title = panel.querySelector('h3');
          const number = panel.querySelector('.pdp2-way__number');
          const panelId = 'pdp2-way-panel-' + (index + 1);
          const tabId = 'pdp2-way-tab-' + (index + 1);
          panel.id = panelId;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabId);
          panel.tabIndex = 0;

          const button = document.createElement('button');
          button.type = 'button';
          button.id = tabId;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', panelId);
          button.innerHTML =
            '<span class="pdp2-ways-nav__number">' + String(index + 1).padStart(2, '0') + '</span>' +
            '<span>' + (title ? title.textContent : 'Option ' + (index + 1)) + '</span>';
          button.addEventListener('click', () => activate(index, false));
          button.addEventListener('keydown', (event) => {
            if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            let next = index;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % buttons.length;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = buttons.length - 1;
            activate(next, true);
          });
          buttons.push(button);
          tabs.append(button);
          stage.append(panel);
          if (number) number.setAttribute('aria-hidden', 'true');
        });

        ways.append(tabs, stage);
        activate(0, false);
      }
    }

    if (ingredientsSection) {
      const ingredientGrid = ingredientsSection.querySelector('.pdp2-ingredients');
      const ingredientDetails = Array.from(ingredientsSection.querySelectorAll('.pdp2-ingredient'));
      if (ingredientGrid) {
        const hint = document.createElement('p');
        hint.className = 'pdp2-ingredients-hint';
        hint.textContent = 'Tap or click an ingredient to see the role it plays in the bar.';
        ingredientGrid.before(hint);
      }
      ingredientDetails.forEach((detail) => {
        detail.addEventListener('toggle', () => {
          if (!detail.open) return;
          ingredientDetails.forEach((other) => {
            if (other !== detail) other.open = false;
          });
        });
      });
    }

    const quiz = root.querySelector('.pdp2-buy .pdp2-quiz');
    if (quiz && comparisonSection) {
      const quizBand = document.createElement('section');
      quizBand.className = 'pdp2-quiz-band';
      quizBand.setAttribute('aria-label', 'Find Your Zen product finder');
      const shell = document.createElement('div');
      shell.className = 'pdp2__shell';
      shell.append(quiz);
      quizBand.append(shell);
      comparisonSection.after(quizBand);
    }

    const tiers = root.querySelector('.pdp2-tiers');
    const mainAtc = root.querySelector('.pdp2-atc');
    const heroPrice = root.querySelector('.pdp2-price');
    const sticky = root.querySelector('.pdp2-sticky');
    const stickyButton = sticky && sticky.querySelector('button');
    const final = finalSection && finalSection.querySelector('.pdp2-final');
    let finalButton = final && final.querySelector('.pdp2-final__button');
    let finalSelection = null;

    if (final && finalButton) {
      const replacement = document.createElement('button');
      replacement.type = 'button';
      replacement.className = finalButton.className;
      replacement.dataset.pdp2Track = 'final_purchase_cta';
      finalButton.replaceWith(replacement);
      finalButton = replacement;

      const finalCopy = final.querySelector('div');
      finalSelection = document.createElement('span');
      finalSelection.className = 'pdp2-final__selection';
      finalSelection.setAttribute('aria-live', 'polite');
      const changeButton = document.createElement('button');
      changeButton.type = 'button';
      changeButton.className = 'pdp2-final__change';
      changeButton.textContent = 'Change quantity';
      changeButton.addEventListener('click', () => {
        tiers && tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const checked = tiers && (tiers.querySelector('[data-zq-input]') || tiers.querySelector('[data-pdp2-tier]:checked'));
        window.setTimeout(() => checked && checked.focus(), 450);
      });
      finalCopy.append(finalSelection, changeButton);

      finalButton.addEventListener('click', () => {
        if (!mainAtc || mainAtc.disabled) {
          tiers && tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        mainAtc.click();
      });
    }

    const syncTier = () => {
      const selected = tiers && tiers.querySelector('[data-pdp2-tier]:checked');
      if (!selected) return;
      const quantity = selected.value;
      const boxWord = quantity === '1' ? 'BOX' : 'BOXES';
      const total = selected.dataset.total || '';
      const bars = selected.dataset.bars || '';
      const perBar = selected.dataset.perBar || '';
      if (heroPrice) {
        const strong = heroPrice.querySelector(':scope > strong');
        const detail = heroPrice.querySelector(':scope > span');
        if (strong) strong.textContent = total;
        if (detail) detail.innerHTML = bars + ' bars · <strong>' + perBar + '</strong>/bar';
      }
      if (finalButton) {
        finalButton.textContent = 'ADD ' + quantity + ' ' + boxWord + ' — ' + total;
        finalButton.setAttribute('aria-label', 'Add ' + quantity + ' ' + boxWord.toLowerCase() + ' to cart for ' + total);
      }
      if (finalSelection) {
        finalSelection.textContent = quantity + ' ' + boxWord.toLowerCase() + ' selected · ' + bars + ' bars · ' + perBar + ' per bar';
      }
      if (mainAtc) {
        mainAtc.setAttribute('aria-label', 'Add ' + quantity + ' ' + boxWord.toLowerCase() + ' to cart for ' + total);
      }
      if (stickyButton) {
        stickyButton.setAttribute('aria-label', 'Add ' + quantity + ' ' + boxWord.toLowerCase() + ' to cart for ' + total);
      }
    };
    if (tiers) tiers.addEventListener('change', syncTier);
    syncTier();

    root.querySelectorAll('.pdp2-inline-atc').forEach((button) => {
      const card = button.closest('.pdp2-cross-sell, .pdp2-compare-wrap');
      const name = card && card.querySelector('h3, h4, a:not(.pdp2-cross-sell__image)');
      const price = card && card.querySelector('.pdp2-cross-sell__price');
      if (name) {
        button.setAttribute(
          'aria-label',
          'Add ' + name.textContent.trim() + (price ? ' for ' + price.textContent.trim() : '') + ' to cart'
        );
      }
    });

    if (sticky) {
      const syncSticky = () => {
        document.body.classList.toggle('pdp2-sticky-active', sticky.classList.contains('is-visible'));
      };
      new MutationObserver(syncSticky).observe(sticky, { attributes: true, attributeFilter: ['class'] });
      syncSticky();
    }

    const updateReviewLinks = () => {
      root.querySelectorAll('a[href="#judgeme_product_reviews"]').forEach((link) => {
        link.href = '#pdp2-reviews';
      });
    };
    updateReviewLinks();

    const moveAndCondenseReviews = () => {
      const widget = document.querySelector('#judgeme_product_reviews');
      if (!widget || !finalSection) return;
      const reviewSection = widget.closest('.shopify-section');
      if (!reviewSection) return;
      reviewSection.id = 'pdp2-reviews';
      reviewSection.classList.add('pdp2-full-reviews');
      if (reviewSection.parentElement !== root || reviewSection.nextElementSibling !== finalSection) {
        finalSection.before(reviewSection);
      }

      const reviews = Array.from(widget.querySelectorAll('.jdgm-rev'));
      reviews.forEach((review, index) => review.classList.toggle('pdp2-review-extra', index >= 3));
      let loadMore = widget.querySelector('.pdp2-review-load-more');
      if (reviews.length > 3 && !loadMore) {
        loadMore = document.createElement('button');
        loadMore.type = 'button';
        loadMore.className = 'pdp2-review-load-more';
        loadMore.textContent = 'Load more reviews';
        loadMore.setAttribute('aria-expanded', 'false');
        const body = widget.querySelector('.jdgm-rev-widg__body') || widget;
        body.append(loadMore);
        loadMore.addEventListener('click', () => {
          const expanded = reviewSection.classList.toggle('is-expanded');
          loadMore.textContent = expanded ? 'Show fewer reviews' : 'Load more reviews';
          loadMore.setAttribute('aria-expanded', String(expanded));
          if (!expanded) reviewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      updateReviewLinks();
    };
    moveAndCondenseReviews();
    window.setTimeout(moveAndCondenseReviews, 800);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion && 'IntersectionObserver' in window) {
      const revealTargets = Array.from(root.querySelectorAll(
        '.pdp2-proof-block, .pdp2-product-experience > section, .pdp2-section--history, .pdp2-section--dark, #pdp2-ingredients, #pdp2-nutrition, #pdp2-compare, .pdp2-quiz-band, #pdp2-cross-sells, #pdp2-faq, .pdp2-full-reviews'
      ));
      revealTargets.forEach((target) => target.setAttribute('data-pdp2-reveal', ''));
      root.classList.add('pdp2-motion-ready');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
      revealTargets.forEach((target) => observer.observe(target));
    }

    if (historySection) {
      const historySteps = historySection.querySelector('.pdp2-history__steps');
      if (historySteps) {
        historySteps.setAttribute('role', 'list');
        historySteps.querySelectorAll('article').forEach((step) => step.setAttribute('role', 'listitem'));
      }
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-pdp2-root], .pdp2').forEach(initPdpEnhancements);
  };
  ready(boot);
  document.addEventListener('shopify:section:load', boot);
})();

/* Keep Shopify section boundaries valid while retaining reviews-before-final-CTA order. */
(() => {
  const run = () => {
    const root = document.querySelector('.pdp2');
    if (!root) return;
    const rootSection = root.closest('.shopify-section');
    const widget = document.querySelector('#judgeme_product_reviews');
    const reviewSection = widget && widget.closest('.shopify-section');
    const finalSource = Array.from(root.querySelectorAll('section')).find((section) => section.querySelector('.pdp2-final'));
    if (!rootSection || !reviewSection || !finalSource) return;

    if (reviewSection.parentElement !== rootSection.parentElement || reviewSection.previousElementSibling !== rootSection) {
      rootSection.after(reviewSection);
    }
    reviewSection.id = 'pdp2-reviews';
    reviewSection.classList.add('pdp2-full-reviews');
    finalSource.classList.add('pdp2-final--source');

    let finalClone = document.querySelector('.pdp2-final-after-reviews');
    if (!finalClone) {
      finalClone = finalSource.cloneNode(true);
      finalClone.classList.remove('pdp2-final--source', 'is-revealed');
      finalClone.classList.add('pdp2-final-after-reviews');
      finalClone.removeAttribute('id');
      finalClone.removeAttribute('aria-labelledby');
      finalClone.removeAttribute('data-pdp2-reveal');
      finalClone.setAttribute('aria-label', 'Final purchase action');
      finalClone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      reviewSection.after(finalClone);

      const mainAtc = root.querySelector('.pdp2-atc');
      const tiers = root.querySelector('.pdp2-tiers');
      const purchaseButton = finalClone.querySelector('.pdp2-final__button');
      const changeButton = finalClone.querySelector('.pdp2-final__change');

      if (purchaseButton) {
        purchaseButton.addEventListener('click', () => {
          if (mainAtc && !mainAtc.disabled) mainAtc.click();
          else tiers && tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
      if (changeButton) {
        changeButton.addEventListener('click', () => {
          tiers && tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const selected = tiers && tiers.querySelector('[data-pdp2-tier]:checked');
          window.setTimeout(() => selected && selected.focus(), 450);
        });
      }

      const syncClone = () => {
        const selected = tiers && tiers.querySelector('[data-pdp2-tier]:checked');
        if (!selected) return;
        const quantity = selected.value;
        const boxWord = quantity === '1' ? 'BOX' : 'BOXES';
        const total = selected.dataset.total || '';
        const bars = selected.dataset.bars || '';
        const perBar = selected.dataset.perBar || '';
        const button = finalClone.querySelector('.pdp2-final__button');
        const selection = finalClone.querySelector('.pdp2-final__selection');
        if (button) {
          button.textContent = 'ADD ' + quantity + ' ' + boxWord + ' — ' + total;
          button.setAttribute('aria-label', 'Add ' + quantity + ' ' + boxWord.toLowerCase() + ' to cart for ' + total);
        }
        if (selection) {
          selection.textContent = quantity + ' ' + boxWord.toLowerCase() + ' selected · ' + bars + ' bars · ' + perBar + ' per bar';
        }
      };
      tiers && tiers.addEventListener('change', syncClone);
      syncClone();
    } else if (finalClone.previousElementSibling !== reviewSection) {
      reviewSection.after(finalClone);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  [80, 950, 1800].forEach((delay) => window.setTimeout(run, delay));
  document.addEventListener('shopify:section:load', run);
})();

/* Keep rendered ingredient terminology aligned with the PDP 2.0 strategy. */
(() => {
  const syncIngredientLabels = () => {
    document.querySelectorAll('.pdp2-gallery-fact strong').forEach((label) => {
      if (label.textContent.trim() === 'Five simple ingredients') {
        label.textContent = 'Five core ingredients';
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncIngredientLabels, { once: true });
  } else {
    syncIngredientLabels();
  }
  document.addEventListener('shopify:section:load', syncIngredientLabels);
})();
